/* Adapter wrapping existing global flutterChart into IChartEngine */
import { IChartEngine, TEngineInitPayload, TEngineBounds, TEngineReplayState } from './IChartEngine';
import { TQuote } from '@smartcharts/core';

// Minimal flutterChart type for adapter (full type in main app src/types/props.types.ts)
declare global {
    interface Window {
        flutterChart?: any;
    }
}

export class FlutterChartEngine implements IChartEngine {
    private _chart() { return window.flutterChart; }
    newChart(payload: TEngineInitPayload): void { this._chart()?.app.newChart(payload); }
    setSymbol(_metadata: { symbol: string; name: string; pip: number }, _decimalPlaces: number): void {
        // Flutter engine handles symbol change via newChart; this is a no-op for now
        // Full integration would update internal pip/symbol state before feed ingestion
    }
    updateTheme(theme: string): void { this._chart()?.config.updateTheme(theme); }
    updateChartStyle(style: string): void { this._chart()?.config.updateChartStyle(style); }
    updateLiveStatus(isLive: boolean): void { this._chart()?.config.updateLiveStatus(isLive); }
    updateLeftMargin(margin?: number): void { this._chart()?.config.updateLeftMargin(margin); }
    updateRightPadding(padding?: number): void { this._chart()?.config.updateRightPadding(padding); }
    setSymbolClosed(isClosed: boolean): void { this._chart()?.config.setSymbolClosed(isClosed); }
    toggleTimeIntervalVisibility(show: boolean): void { this._chart()?.config.toggleTimeIntervalVisibility(show); }
    toggleDataFitMode(isEnabled: boolean): void { this._chart()?.app.toggleDataFitMode(isEnabled); }
    toggleXScrollBlock(block: boolean): void { this._chart()?.app.toggleXScrollBlock(block); }
    onNewTick(q: TQuote): void { this._chart()?.feed.onNewTick(q); }
    onNewCandle(q: TQuote): void { this._chart()?.feed.onNewCandle(q); }
    onTickHistory(quotes: TQuote[], append: boolean): void { this._chart()?.feed.onTickHistory(quotes, append); }
    resetSeries(): void { this._chart()?.replay.resetSeries(); }
    applyTick(q: TQuote, replace?: boolean): void { this._chart()?.replay.applyTick(q, replace); }
    applyTickBatch(quotes: TQuote[], append?: boolean): void { this._chart()?.replay.applyTickBatch(quotes, append); }
    setCursorTime(epoch: number): void { this._chart()?.replay.setCursorTime(epoch); }
    rebuildToEpoch(targetEpoch: number, allTicks: TQuote[]): void { this._chart()?.replay.rebuildToEpoch(targetEpoch, allTicks); }
    getReplayState(): TEngineReplayState { return this._chart()?.replay.getReplayState(); }
    clearReplayState(): void { this._chart()?.replay.clearReplayState(); }
    addOrUpdateIndicator(config: string, index?: number): void { this._chart()?.app.addOrUpdateIndicator(config, index); }
    removeIndicator(index: number): void { this._chart()?.indicators.removeIndicator(index); }
    clearIndicators(): void { this._chart()?.indicators.clearIndicators(); }
    addOrUpdateDrawing(config: string, index?: number): void { this._chart()?.drawingTool.addOrUpdateDrawing(config, index); }
    removeDrawingTool(index: number): void { this._chart()?.drawingTool.removeDrawingTool(index); }
    clearDrawingTool(): void { this._chart()?.drawingTool.clearDrawingTool(); }
    editDrawing(config: string, index: number): void { this._chart()?.drawingTool.editDrawing(config, index); }
    getXFromEpoch(epoch: number): number { return this._chart()?.app.getXFromEpoch(epoch); }
    getYFromQuote(quote: number): number { return this._chart()?.app.getYFromQuote(quote); }
    getEpochFromX(x: number): number { return this._chart()?.app.getEpochFromX(x); }
    getQuoteFromY(y: number): number { return this._chart()?.app.getQuoteFromY(y); }
    scroll(pxShift: number): void { this._chart()?.app.scroll(pxShift); }
    scrollToLastTick(): void { this._chart()?.app.scrollToLastTick(); }
    scale(scale: number): number { return this._chart()?.app.scale(scale); }
    getBounds(): TEngineBounds { return { first: null, last: null }; }
}

export const flutterChartEngine = new FlutterChartEngine();
